// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useState, useEffect } from "react"
import { equals } from "ramda"
import { usePrevious } from "react-use"
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc"
import { reorderArray } from "lib/utils"
import {
  List,
  StyledUnfavouriteIcon,
  DraggableHandler,
  ListItemDraggable,
  ListItemButton,
} from "./styles"
import { ListItemFavouriteType } from "./types"
import { t } from "@lingui/macro"
import { ListItemDescription, ListItemLabel } from "../styles"

const DragHandle = SortableHandle(DraggableHandler)

const SortableItem = SortableElement(({
  id,
  refId,
  isAdded,
  label,
  description,
  isDragging,
  isActive,
  onMouseMove,
  onAddItemToDestination,
  onUnfavouriteItem,
}: {
  id: string,
  refId: string,
  isAdded: boolean,
  label: string,
  description: string | undefined,
  isDragging: boolean,
  isActive: boolean,
  onMouseMove: () => void,
  onAddItemToDestination: (refId: string) => void,
  onUnfavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void,
}) => (
  <ListItemDraggable
    isAdded={isAdded}
    isActive={isActive}
    id={isActive ? id : ""}
    onClick={(): void => {
      if (!isAdded) {
        onAddItemToDestination(refId)
      }
    }}
    onMouseMove={onMouseMove}
    title={isAdded ? t({ id: "label_already_added", message: "Already added" }) : ""}
    isDragging={isDragging}
  >
    <DragHandle/>
    <ListItemButton
      type="button"
      title={t({ id: "label_unfavourite", message: "Unfavourite" })}
      onClick={(event): void => onUnfavouriteItem(event, id)}
    >
      <StyledUnfavouriteIcon name="unfavourite"/>
    </ListItemButton>
    <ListItemLabel>{label}</ListItemLabel>
    {description && <ListItemDescription title={description}>{description}</ListItemDescription>}
  </ListItemDraggable>
))

const Container = SortableContainer(({ children }: { children: React.ReactNode[] }) => {
  return (
    <List>
      {children}
    </List>
  )
})

type Props = {
  header: React.ReactNode,
  list: ListItemFavouriteType[],
  addedItemIds: string[],
  activeId: string | undefined,
  onMouseMove: () => void,
  onAddItemToDestination: (refId: string) => void,
  onUnfavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void,
  onReorderDraggableItem: (id: string, newIndex: number) => void,
}

const SearchDropdownDraggableList = ({
  header,
  list = [],
  addedItemIds,
  activeId,
  onMouseMove,
  onAddItemToDestination,
  onUnfavouriteItem,
  onReorderDraggableItem,
}: Props): React.ReactElement<Props> => {
  const [isDragging, setIsDragging] = useState(false)
  const [items, setItems] = useState<ListItemFavouriteType[]>(list)
  const onSortStart = (): void => {
    setIsDragging(true)
  }
  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }): void => {
    setIsDragging(false)
    if (oldIndex !== newIndex) {
      const reorderedList = reorderArray(
        list,
        oldIndex,
        newIndex,
      ).map((item: ListItemFavouriteType, index: number): ListItemFavouriteType => ({
        ...item,
        siteListIndex: index,
      }))
      // update local state immediately to avoid content jump
      setItems(reorderedList)
      const listItem = list[oldIndex]
      if (listItem !== undefined) {
        onReorderDraggableItem(listItem.id, newIndex)
      }
    }
  }
  const prevList = usePrevious(list)
  useEffect(() => {
    if (!equals(list, prevList)) {
      setItems(list)
    }
  }, [list])
  return (
    <>
      {header}
      <Container onSortStart={onSortStart} onSortEnd={onSortEnd} useDragHandle helperClass="dragging-helper-class">
        {items.map(({ id, refId, label, description }: ListItemFavouriteType, index: number) => (
          <SortableItem
            key={id}
            index={index}
            id={id}
            refId={refId}
            isAdded={addedItemIds.some(id => id === refId)}
            label={label}
            description={description}
            isDragging={isDragging}
            isActive={activeId === id}
            onMouseMove={onMouseMove}
            onAddItemToDestination={onAddItemToDestination}
            onUnfavouriteItem={onUnfavouriteItem}
          />
        ))}
      </Container>
    </>
  )
}

export default SearchDropdownDraggableList
